import { useCallback, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import type {
	StoreChatItem,
	StoreFollowUp,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessagesActions } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';

import type { AtlassianTeamUpdateArchiveHydratedAction } from '../types';
import { isActionReadonly } from '../utils/action-state';

const messages = defineMessages({
	runCleanUpTeamsAgain: {
		id: 'conversation-assistant-message-actions.atlassian-team-update-archive.run-clean-up-teams-again',
		defaultMessage: 'Run /clean-up-teams again',
		description:
			'Text for a follow-up suggestion button shown after the user successfully archives teams via the Clean Up Teams skill. Clicking it re-invokes the /clean-up-teams skill.',
	},
});

const isCleanUpTeamsFollowUp = (item: StoreChatItem, text: string): boolean => {
	if (item.type !== 'FOLLOW_UP') {
		return false;
	}
	const followUps = item.content.message.follow_ups;
	if (!followUps || followUps.length !== 1) {
		return false;
	}
	return followUps[0]?.text === text;
};

export interface UseAppendCleanUpTeamsFollowUpParams {
	conversationId: string | undefined;
	actions: AtlassianTeamUpdateArchiveHydratedAction[];
	getReadonly: (action: MessageAction) => boolean;
}

export const useAppendCleanUpTeamsFollowUp = ({
	conversationId,
	actions,
	getReadonly,
}: UseAppendCleanUpTeamsFollowUpParams): {
	maybeAppendFollowUp: (completedActions: AtlassianTeamUpdateArchiveHydratedAction[]) => void;
} => {
	const { formatMessage } = useIntl();
	const { getMessages, setMessages } = useChatMessagesActions();

	const completedTeamIdsRef = useRef<Set<string>>(new Set());

	const maybeAppendFollowUp = useCallback(
		(completedActions: AtlassianTeamUpdateArchiveHydratedAction[]) => {
			if (!conversationId) {
				return;
			}

			completedActions.forEach((a) => completedTeamIdsRef.current.add(a.data.teamId));
			const isLastAction = actions.every(
				(a) => completedTeamIdsRef.current.has(a.data.teamId) || isActionReadonly(a, getReadonly),
			);
			if (!isLastAction) {
				return;
			}

			const followUpText = formatMessage(messages.runCleanUpTeamsAgain);
			const currentMessages = getMessages(conversationId);
			const dedupedMessages = currentMessages.filter(
				(m) => !isCleanUpTeamsFollowUp(m, followUpText),
			);

			const followUpItem: StoreFollowUp = {
				type: 'FOLLOW_UP',
				content: {
					type: 'FOLLOW_UP_QUERIES',
					message: {
						follow_ups: [{ type: 'SYNTHETIC', text: followUpText }],
					},
					millisOffset: 0,
				},
				id: Date.now(),
			};

			setMessages(conversationId, [...dedupedMessages, followUpItem]);
		},
		[conversationId, actions, getReadonly, formatMessage, getMessages, setMessages],
	);

	return { maybeAppendFollowUp };
};
